import React from 'react';
import {NavLink, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getCurrentNetworkLevel, getCurrentUsername, isPlayer} from '../../utils/currentUser';
import {cifra, importo, leggiCifreConto} from '../../utils/conto';
import IconaNova from './IconaNova';
import {vociColonnaConto, vociDellaRete} from './voci';
import {fedeltaInMostra, livelloDelGiocatore, puntiTesto, useFedelta} from '../Pagine/Fedelta';

/**
 * LA COLONNA DEL CONTO (stile Nova, dal computer): a sinistra delle pagine
 * del conto, come nel disegno Conto. Chi sei (iniziale, nome, livello), i
 * saldi, le voci del conto e «Esci». Sotto i 1024px non c'e' (Nova.css):
 * sul telefono il conto resta com'e', col pannello dal basso.
 *
 * LE VOCI SONO QUELLE DEL GETTONE (utils/permessi.js), le stesse del
 * pannello Conto: una voce a chi non ha il permesso portava a «non
 * disponibile». Gioco responsabile e' di tutti. La lista e' in voci.js
 * (vociColonnaConto): la tendina «Account» in testata usa la stessa.
 *
 * SOTTO, PER CHI HA QUALCUNO SOTTO DI SE', il riquadro «Rete»: Lista utenti,
 * Giroconto, Report, ognuna a chi ha la sua voce. A un giocatore il riquadro
 * non compare.
 *
 * I SALDI sono le cifre del conto (utils/conto.js), rilette quando il conto
 * cambia (balanceUpdated): quello che il servizio non manda non c'e'. Col
 * programma fedelta' in mostra (Pagine/Fedelta.js) il livello e' quello del
 * programma, coi punti, e porta a /fedelta; se no la posizione nella rete.
 *
 * `children` va in fondo, sotto «Esci»: Gioco responsabile ci mette «In
 * questa pagina», le sezioni del suo testo (Vetrina/PaginaTesto.js).
 */
export default function ColonnaConto({userService, children}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [conto, setConto] = React.useState(null);
  React.useEffect(() => {
    let vivo = true;
    const rileggi = () => leggiCifreConto().then((d) => { if (vivo && d) setConto(d); });
    rileggi();
    window.addEventListener('balanceUpdated', rileggi);
    window.addEventListener('giocataCambiata', rileggi);
    return () => {
      vivo = false;
      window.removeEventListener('balanceUpdated', rileggi);
      window.removeEventListener('giocataCambiata', rileggi);
    };
  }, []);

  const chi = (userService && typeof userService.getUsername === 'function' && userService.getUsername()) || getCurrentUsername() || '';
  const fedelta = useFedelta(isPlayer() && fedeltaInMostra());
  const livelloFedelta = fedelta.stato === 'pronto' ? livelloDelGiocatore(fedelta.dati) : null;
  const livello = getCurrentNetworkLevel();
  const saldo = cifra(conto, isPlayer() ? 'userBalance' : 'available');
  const bonus = cifra(conto, 'bonusBalance');
  const valuta = conto && conto.currency;
  const esci = () => {
    try {
      if (userService && typeof userService.doLogout === 'function') userService.doLogout(window.localStorage.getItem('CLIENT_SESSION'));
    } finally {
      navigate('/');
    }
  };
  const rete = vociDellaRete(t);
  const voce = (to, icona, testo, oro) => (
    <NavLink key={to} to={to} className={({isActive}) => 'nova-conto-voce' + (oro ? ' oro' : '') + (isActive ? ' active' : '')}>
      <IconaNova nome={icona} misura={20}/>{testo}
    </NavLink>
  );
  return (
    <aside className={'nova-conto'} aria-label={t('Il mio conto')}>
      <div className={'nova-conto-chi'}>
        <span className={'nova-avatar'} aria-hidden={'true'}>{(chi[0] || '?').toUpperCase()}</span>
        <span className={'nova-conto-nome'}>
          <b>{chi}</b>
          {livelloFedelta ? (
            <NavLink to={'/fedelta'} className={'nova-conto-livello'}>
              {t('Livello {{livello}} · {{punti}} punti', {livello: livelloFedelta.nome, punti: puntiTesto(fedelta.dati.punti)})}
            </NavLink>
          ) : livello && <small>{t('Livello {{livello}}', {livello})}</small>}
        </span>
      </div>
      {saldo !== null && (
        <div className={'nova-conto-saldi'}>
          <div className={'nova-conto-saldo'}>
            <small>{isPlayer() ? t('Saldo reale') : t('Disponibile')}</small>
            <b>{importo(saldo, valuta)}</b>
          </div>
          {bonus !== null && (
            <div className={'nova-conto-saldo bonus'}>
              <small>{t('Saldo bonus')}</small>
              <b>{importo(bonus, valuta)}</b>
            </div>
          )}
        </div>
      )}
      <nav className={'nova-conto-voci'}>
        {vociColonnaConto(t).map((v) => voce(v.to, v.icona, v.testo, v.oro))}
      </nav>
      {/* Le voci della Rete vengono da voci.js, come nel menu: li' il Report
          chiede anche che la skin abbia il casino'. */}
      {rete.length > 0 && (
        <nav className={'nova-conto-voci nova-conto-rete'} aria-label={t('Rete')}>
          <p className={'nova-etichetta'}>{t('Rete')}</p>
          {rete.map((v) => voce(v.to, v.icona, v.testo))}
        </nav>
      )}
      <button type={'button'} className={'nova-foglio-esci nova-conto-esci'} onClick={esci}>
        <IconaNova nome={'esci'} misura={18}/>{t('Esci')}
      </button>
      {children}
    </aside>
  );
}
